import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Paper,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import config from '../../config';
import { normalizeImageUrl } from '../../components/Post';
import './Profile.css';

const CorporateProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser, token } = useAuth();
  
  // プロフィールデータの状態
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 現在のユーザーが表示しているプロフィールが自分自身かどうかを判定
  const isCurrentUserProfile = currentUser && currentUser.username === username;
  
  // プロフィールデータの取得
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 企業プロフィールデータを取得
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        if (response.data && response.data.status === 'success') {
          setProfileData(response.data.data);
        } else {
          setError('プロフィール情報の取得に失敗しました。');
        }
        
      } catch (err) {
        
        setError('プロフィールデータの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchProfileData();
    } else {
      setError('閲覧するにはログインが必要です。');
      setTimeout(() => navigate('/login'), 1500);
    }
  }, [token, username, navigate]);
  
  // ローディング表示
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }
  
  // エラー表示
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  // プロフィールデータがない場合
  if (!profileData) {
    return (
      <Box p={3}>
        <Alert severity="warning">プロフィール情報が見つかりません。プロフィールを設定してください。</Alert>
        <Box mt={2}>
          <Button 
            variant="contained" 
            color="primary" 
            component={Link} 
            to="/corporate/profile/edit"
          >
            プロフィールを編集
          </Button>
        </Box>
      </Box>
    );
  }
  
  // 営業時間のフォーマット
  const formatBusinessHours = () => {
    const start = profileData.businessHours?.start || '設定なし';
    const end = profileData.businessHours?.end || '設定なし';
    return `${start} 〜 ${end}`;
  };
  
  return (
    <Box className="corporate-profile">
      {/* バナー（カバー画像） */}
      <Box className="profile-banner" sx={{ 
        height: '200px', 
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '4px 4px 0 0'
      }}>
        {profileData.bannerUrl ? (
          <img 
            src={profileData.bannerUrl} 
            alt="企業バナー" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          />
        ) : (
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            backgroundColor: '#1976d2',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Typography variant="h6" color="white">バナー画像がありません</Typography>
          </Box>
        )}
        
        {/* 編集ボタン */}
        {isCurrentUserProfile && (
          <IconButton 
            component={Link} 
            to="/corporate/profile/edit" 
            sx={{ 
              position: 'absolute',
              top: '10px',
              right: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>
      
      <Paper className="profile-paper" sx={{ position: 'relative', padding: 3, mb: 3 }}>
        {/* ロゴ（アバター） */}
        <Avatar
          src={normalizeImageUrl(profileData.logoUrl)}
          alt={profileData.corporateName}
          sx={{
            width: 120,
            height: 120,
            border: '4px solid white',
            position: 'absolute',
            top: -60,
            left: 24,
            backgroundColor: '#1976d2'
          }}
        />
        
        {/* プロフィール編集ボタン */}
        {isCurrentUserProfile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              component={Link}
              to="/corporate/profile/edit"
            >
              プロフィールを編集
            </Button>
          </Box>
        )}
        
        {/* 企業名と基本情報 */}
        <Box mt={8}>
          <Typography variant="h4" component="h1" gutterBottom>
            {profileData.corporateName}
          </Typography>
          
          {profileData.description && (
            <Typography variant="body1" paragraph>
              {profileData.description}
            </Typography>
          )}
          
          {/* 企業情報の詳細 */}
          <Grid container spacing={2} mt={1}>
            {/* 業種 */}
            {profileData.industry && profileData.industry.length > 0 && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" component="span">
                    業種:
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {profileData.industry.map((ind, index) => (
                    <Chip key={index} label={ind} color="primary" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}
            
            {/* 所在地 */}
            {profileData.location && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">{profileData.location}</Typography>
                </Box>
              </Grid>
            )}
            
            {/* 都道府県・市区町村 */}
            {profileData.prefecture && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    {profileData.prefecture.name}
                    {profileData.cities && profileData.cities.length > 0 && (
                      ` (${profileData.cities.map(city => city.name).join(', ')})`
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {/* 電話番号 */}
            {profileData.phoneNumber && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    <a 
                      href={`tel:${profileData.phoneNumber}`}
                      style={{
                        color: '#1976d2',
                        fontWeight: 'bold',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {profileData.phoneNumber}
                    </a>
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {/* Webサイト */}
            {profileData.website && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <WebsiteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    <a href={profileData.website} target="_blank" rel="noopener noreferrer">
                      {profileData.website}
                    </a>
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {/* 営業時間 */}
            {(profileData.businessHours?.start || profileData.businessHours?.end) && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">営業時間: {formatBusinessHours()}</Typography>
                </Box>
              </Grid>
            )}
            
            {/* 求人URL */}
            {(profileData.jobPostingUrl || profileData.job_posting_url) && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    <a 
                      href={profileData.jobPostingUrl || profileData.job_posting_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        color: '#1976d2',
                        fontWeight: 'bold',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      🔗 求人情報を見る
                    </a>
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {/* オフィシャルLINE */}
            {(profileData.officialLine || profileData.official_line) && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <svg 
                    viewBox="0 0 24 24" 
                    width="24" 
                    height="24" 
                    fill="#00B900" 
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.348 0 .63.285.63.63 0 .349-.282.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"></path>
                  </svg>
                  <Typography variant="body1">
                    <a 
                      href={profileData.officialLine || profileData.official_line} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{
                        color: '#00B900',
                        fontWeight: 'bold',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      🔗 公式LINEを開く
                    </a>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
      
      {/* 追加のコンテンツや広告などをここに配置 */}
      <Paper className="profile-paper" sx={{ padding: 3 }}>
        <Typography variant="h6" gutterBottom>
          最新の広告
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            component={Link} 
            to="/corporate/advertisements"
          >
            広告を管理する
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CorporateProfile; 